/**
 * Minimal deterministic value noise.
 *
 * This is placeholder terrain for phase 0 - real world generation (biomes,
 * caves, rivers, the Carpathians) lands in phase 3 and will replace this file.
 * It exists so the first thing you see is a landscape, not a flat plane.
 */

/**
 * Integer hash -> [0, 1). Deterministic, no allocation, no state.
 *
 * Exported as `hashInt` for worldgen decisions - which cell gets a tree, how
 * tall it is. Determinism matters more here than anywhere else: the save
 * format stores only what the player changed, so regenerating the world has to
 * plant exactly the same forest.
 */
function hash2(x: number, y: number, seed: number): number {
  let h = x * 374761393 + y * 668265263 + seed * 1274126177;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return (h >>> 0) / 4294967296;
}

export { hash2 as hashInt };

/** Smoothstep - gives value noise its rounded look instead of linear creases. */
function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/** 2D value noise in [0, 1). */
export function valueNoise2(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = smooth(x - xi);
  const yf = smooth(y - yi);

  const v00 = hash2(xi, yi, seed);
  const v10 = hash2(xi + 1, yi, seed);
  const v01 = hash2(xi, yi + 1, seed);
  const v11 = hash2(xi + 1, yi + 1, seed);

  const a = v00 + (v10 - v00) * xf;
  const b = v01 + (v11 - v01) * xf;
  return a + (b - a) * yf;
}

/**
 * The same hash with a third coordinate.
 *
 * Caves are the reason: a cave is a hole that goes *down* as well as along,
 * and a field that only knows x and z can only ever cut the same hole
 * through every layer at once - a shaft, not a cave.
 */
function hash3(x: number, y: number, z: number, seed: number): number {
  let h = x * 374761393 + y * 668265263 + z * 2246822519 + seed * 1274126177;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return (h >>> 0) / 4294967296;
}

/** 3D value noise in [0, 1). Eight corners of a cell, smoothed between. */
export function valueNoise3(
  x: number, y: number, z: number, seed: number,
): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const zi = Math.floor(z);
  const xf = smooth(x - xi);
  const yf = smooth(y - yi);
  const zf = smooth(z - zi);

  const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
  const face = (dz: number): number => {
    const v00 = hash3(xi, yi, zi + dz, seed);
    const v10 = hash3(xi + 1, yi, zi + dz, seed);
    const v01 = hash3(xi, yi + 1, zi + dz, seed);
    const v11 = hash3(xi + 1, yi + 1, zi + dz, seed);
    return lerp(lerp(v00, v10, xf), lerp(v01, v11, xf), yf);
  };
  return lerp(face(0), face(1), zf);
}

/** Stacked octaves of the 3D field. */
export function fbm3(
  x: number, y: number, z: number, seed: number, octaves = 3,
): number {
  let sum = 0;
  let amp = 1;
  let freq = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise3(x * freq, y * freq, z * freq, seed + i * 8191) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

/** Stacked octaves - large shapes plus fine detail. */
export function fbm2(
  x: number,
  y: number,
  seed: number,
  octaves = 4,
  lacunarity = 2,
  gain = 0.5,
): number {
  let sum = 0;
  let amp = 1;
  let freq = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise2(x * freq, y * freq, seed + i * 1013) * amp;
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

/**
 * The same field as `valueNoise3`, read straight up one column.
 *
 * Carving caves asks the 3D field of every cell underground, and that is
 * millions of questions for one world: the first version took a world from
 * 1.3 seconds to start to 9.4. But a column has one x and one z, so seven of
 * the eight corners of every cell are shared with the cell above and below -
 * only the lattice level changes, and the lattice is twenty blocks tall.
 *
 * So interpolate x and z once per lattice level, keep the two levels the
 * reader is between, and a column of forty cells costs three of those
 * levels instead of forty whole samples. Trilinear interpolation does not
 * care what order the axes are taken in, so this answers exactly what
 * `valueNoise3(x, y / tall, z, seed)` answers - there is a check that says so.
 *
 * @param tall how many blocks to a lattice level, so the caller can pass
 *   whole-world y.
 */
export function noiseColumn3(
  x: number, z: number, tall: number, seed: number,
): (y: number) => number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const xf = smooth(x - xi);
  const zf = smooth(z - zi);

  const level = (yi: number): number => {
    const v00 = hash3(xi, yi, zi, seed);
    const v10 = hash3(xi + 1, yi, zi, seed);
    const v01 = hash3(xi, yi, zi + 1, seed);
    const v11 = hash3(xi + 1, yi, zi + 1, seed);
    const a = v00 + (v10 - v00) * xf;
    const b = v01 + (v11 - v01) * xf;
    return a + (b - a) * zf;
  };

  // The two levels last asked for. Reading a column goes up, so the usual
  // move is one level on and the upper one becomes the lower.
  let held = Number.NaN;
  let below = 0;
  let above = 0;
  return (y: number): number => {
    const at = y / tall;
    const yi = Math.floor(at);
    if (yi !== held) {
      if (yi === held + 1) below = above;
      else below = level(yi);
      above = level(yi + 1);
      held = yi;
    }
    return below + (above - below) * smooth(at - yi);
  };
}
